/**
 * TravelAdminGenerate — /admin/travel/generate
 * Admin-only. Triggers batch AI article generation for travel
 * operators that don't yet have an article. Shows queue status
 * per travel type. Runs synchronously in small batches so the
 * admin can control LLM spend.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, ArrowLeft, PlayCircle, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import TravelCategoryNav from "@/components/TravelCategoryNav";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  airline: "Airlines", airport: "Airports", rail_operator: "Rail Operators",
  rail_station: "Rail Stations", bus_terminal: "Bus Terminals",
  boat_terminal: "Boat / Ferry", seaport: "Seaports", taxi_service: "Taxi",
};

export default function TravelAdminGenerate() {
  const { user, token } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [batchType, setBatchType] = useState(null);

  const refresh = () => {
    axios.get(`${API}/api/travel/generate-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setStatus(r.data.by_type))
      .catch(() => {});
  };
  useEffect(() => { if (token) refresh(); }, [token]);

  if (!user?.is_admin && user?.role !== "admin") {
    return (
      <div className="max-w-xl mx-auto py-16 px-4 text-center">
        <h1 className="font-display text-3xl mb-2">Admin only</h1>
        <Link to="/travel" className="wofy-link">← Travel home</Link>
      </div>
    );
  }

  const runBatch = async (type = null, limit = 5) => {
    setBusy(true); setBatchType(type);
    try {
      const r = await axios.post(
        `${API}/api/travel/generate-missing?limit=${limit}${type ? `&type=${type}` : ""}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = r.data;
      toast.success(`Batch: ${d.succeeded} succeeded · ${d.failed} failed · ${d.remaining} remaining`);
      setLog(prev => [{ at: new Date().toLocaleTimeString(), ...d, type: type || "any" }, ...prev].slice(0, 20));
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch failed");
    } finally { setBusy(false); setBatchType(null); }
  };

  const totalMissing = status ? Object.values(status).reduce((n, v) => n + v.missing, 0) : 0;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="travel-admin-generate">
      <TravelCategoryNav />
      <Link to="/travel" className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100 mb-4 mt-4">
        <ArrowLeft className="w-4 h-4" /> Travel
      </Link>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="overline text-xs opacity-70">ADMIN · TRAVEL</div>
          <h1 className="font-display text-3xl mb-1">Auto-Generate Travel Profiles</h1>
          <p className="text-sm opacity-70">Runs Claude Sonnet 4.5 to write encyclopedia-style articles for every operator that doesn't yet have one. Small batches keep LLM spend predictable.</p>
        </div>
        <button onClick={refresh} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full border" style={{ borderColor: "var(--wofy-border)" }} data-testid="refresh-btn">
          <RefreshCw className="w-3.5 h-3.5" />Refresh
        </button>
      </div>

      {/* Status */}
      <section className="wofy-card !p-5 mb-6" data-testid="status-panel">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-display text-xl">Queue status</h2>
          <div className="text-sm">
            <b>{totalMissing}</b> operators missing an article
          </div>
        </div>
        {!status && <div className="opacity-60 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Loading…</div>}
        {status && (
          <div className="grid gap-2">
            {Object.entries(status).sort((a, b) => b[1].missing - a[1].missing).map(([t, v]) => (
              <div key={t} className="flex items-center gap-3 p-2 rounded-lg" style={{ background: "var(--wofy-surface)" }} data-testid={`status-row-${t}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{TYPE_LABELS[t] || t}</div>
                  <div className="text-xs opacity-70">
                    <span className="font-mono">{v.with_article}</span> of <span className="font-mono">{v.total}</span> have articles
                  </div>
                </div>
                <div className="text-sm font-mono opacity-70" style={{ minWidth: 50, textAlign: "right" }}>
                  {v.missing} left
                </div>
                {v.missing > 0 && (
                  <button
                    onClick={() => runBatch(t, 5)}
                    disabled={busy}
                    className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-white disabled:opacity-40"
                    style={{ background: "var(--wofy-primary)" }}
                    data-testid={`run-${t}`}
                  >
                    {busy && batchType === t ? <><Loader2 className="w-3 h-3 animate-spin" />Running…</> : <><PlayCircle className="w-3 h-3" />Run 5</>}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 pt-4 border-t flex gap-2 flex-wrap" style={{ borderColor: "var(--wofy-border)" }}>
          <button onClick={() => runBatch(null, 10)} disabled={busy || totalMissing === 0} className="wofy-btn-primary inline-flex items-center gap-2 disabled:opacity-40" data-testid="run-any-10">
            {busy && batchType === null ? <><Loader2 className="w-4 h-4 animate-spin" />Running…</> : <><Sparkles className="w-4 h-4" />Run batch of 10 (any type)</>}
          </button>
          <button onClick={() => runBatch(null, 25)} disabled={busy || totalMissing === 0} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border disabled:opacity-40" style={{ borderColor: "var(--wofy-border)" }} data-testid="run-any-25">
            Run batch of 25
          </button>
        </div>
      </section>

      {/* Log */}
      {log.length > 0 && (
        <section className="wofy-card !p-5" data-testid="log-panel">
          <h2 className="font-display text-xl mb-3">Recent batches</h2>
          <ul className="text-sm space-y-1 font-mono">
            {log.map((r, i) => (
              <li key={i} className="opacity-80">
                <span className="opacity-50">{r.at}</span> · type={r.type} · processed={r.processed} · <span className="text-green-700">ok={r.succeeded}</span> · <span className="text-red-700">fail={r.failed}</span> · remaining={r.remaining}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
